/**
 * Parent Zone API client — Phase 5.2.
 *
 * Narrow client over:
 *   - GET    /v1/parent/pin/status     detect whether setup is needed
 *   - POST   /v1/parent/pin               set initial PIN
 *   - POST   /v1/parent/auth              verify PIN → session
 *   - PUT    /v1/parent/pin               change PIN
 *   - DELETE /v1/parent/pin               delete PIN
 *   - GET    /v1/parent/session           verify session
 *   - GET    /v1/settings/:child_id       get settings
 *   - PUT    /v1/settings/:child_id       update settings
 *   - GET    /v1/history/:child_id/export CSV
 *
 * Same pattern as other api/ files — thin wrapper around apiFetch
 * with type-safe response handling.
 *
 * Session-gated endpoints (PUT/DELETE/GET session, GET/PUT settings,
 * GET export) require the caller to attach X-Parent-Session via
 * apiFetch's sessionToken option. The PIN endpoints (set/auth) don't
 * need a session.
 */

import { apiFetch } from './client';

// ----------------------------------------------------------------------------
// PIN
// ----------------------------------------------------------------------------

export type PinStatusResult =
  | { ok: true; hasPin: boolean }
  | { ok: false; error: string; message?: string };

/** GET /v1/parent/pin/status — detect setup vs verify before asking for input. */
export async function getPinStatus(): Promise<PinStatusResult> {
  try {
    const res = await apiFetch<{ ok: true; hasPin: boolean }>('/v1/parent/pin/status');
    return { ok: true, hasPin: res.hasPin === true };
  } catch (err) {
    const e = err as { status?: number; message?: string };
    return {
      ok: false,
      error: 'status_failed',
      message: e.message ?? 'Gagal mengecek status PIN.',
    };
  }
}

export type PinResult =
  | { ok: true; createdAt: string }
  | { ok: false; error: string; message?: string };

/** POST /v1/parent/pin — set initial 4-digit PIN. */
export async function setPin(pin: string): Promise<PinResult> {
  try {
    const res = await apiFetch<{ ok: true; createdAt: string }>(
      '/v1/parent/pin',
      { method: 'POST', body: { pin } },
    );
    return { ok: true, createdAt: res.createdAt };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Gagal menyimpan PIN.';
    return { ok: false, error: 'PinSetFailed', message };
  }
}

export type AuthPinResult =
  | { ok: true; sessionToken: string; expiresAt: string; expiresIn: number }
  | {
      ok: false;
      error: 'wrong_pin' | 'locked' | 'no_pin' | 'forbidden_pin' | 'unknown';
      message?: string;
      failedAttempts?: number;
      remainingAttempts?: number;
      remainingSeconds?: number;
      isPermanent?: boolean;
    };

/** POST /v1/parent/auth — verify PIN, return session token. */
export async function authPin(pin: string): Promise<AuthPinResult> {
  try {
    const res = await apiFetch<{
      ok: true;
      sessionToken: string;
      expiresAt: string;
      expiresIn: number;
    }>('/v1/parent/auth', { method: 'POST', body: { pin } });
    return {
      ok: true,
      sessionToken: res.sessionToken,
      expiresAt: res.expiresAt,
      expiresIn: res.expiresIn,
    };
  } catch (err: unknown) {
    // Parse structured error from ApiError (status + body).
    const e = err as { code?: string; status?: number; message?: string; body?: Record<string, unknown> };
    const body = e.body ?? {};
    if (e.status === 423) {
      return {
        ok: false,
        error: 'locked',
        isPermanent: body.isPermanent === true,
        remainingSeconds: typeof body.remainingSeconds === 'number' ? body.remainingSeconds : 0,
        message: e.message,
      };
    }
    if (e.status === 401) {
      return {
        ok: false,
        error: 'wrong_pin',
        failedAttempts: typeof body.failedAttempts === 'number' ? body.failedAttempts : undefined,
        remainingAttempts: typeof body.remainingAttempts === 'number' ? body.remainingAttempts : undefined,
        message: e.message,
      };
    }
    if (e.status === 404) {
      return { ok: false, error: 'no_pin', message: e.message };
    }
    if (e.status === 400) {
      return { ok: false, error: 'forbidden_pin', message: e.message };
    }
    return {
      ok: false,
      error: 'unknown',
      message: e.message ?? 'Gagal memverifikasi PIN.',
    };
  }
}

export type ChangePinResult =
  | { ok: true; sessionToken: string; expiresAt: string }
  | { ok: false; error: string; message?: string };

/** PUT /v1/parent/pin — change PIN. */
export async function changePin(
  sessionToken: string,
  currentPin: string,
  newPin: string,
): Promise<ChangePinResult> {
  try {
    const res = await apiFetch<{ ok: true; sessionToken: string; expiresAt: string }>(
      '/v1/parent/pin',
      { method: 'PUT', body: { currentPin, newPin }, sessionToken },
    );
    return {
      ok: true,
      sessionToken: res.sessionToken,
      expiresAt: res.expiresAt,
    };
  } catch (err) {
    const e = err as { status?: number; message?: string };
    return {
      ok: false,
      error: e.status === 401 ? 'wrong_current_pin' : 'change_failed',
      message: e.message,
    };
  }
}

export type DeletePinResult =
  | { ok: true; deleted: boolean }
  | { ok: false; error: string; message?: string };

/** DELETE /v1/parent/pin — for "Lupa PIN" flow. */
export async function deletePin(sessionToken: string): Promise<DeletePinResult> {
  try {
    const res = await apiFetch<{ ok: true; deleted: boolean }>(
      '/v1/parent/pin',
      { method: 'DELETE', sessionToken },
    );
    return { ok: true, deleted: res.deleted };
  } catch (err) {
    const e = err as { status?: number; message?: string };
    return { ok: false, error: 'delete_failed', message: e.message };
  }
}

// ----------------------------------------------------------------------------
// Settings
// ----------------------------------------------------------------------------

export interface AppSettings {
  language: 'bi' | 'en';
  theme: 'system' | 'light' | 'dark';
  textScale: 'normal' | 'large' | 'xlarge';
  reduceMotion: boolean;
  highContrast: boolean;
  notificationDaily: boolean;
  updatedAt: string | null;
}

export type GetSettingsResult =
  | { ok: true; settings: AppSettings }
  | { ok: false; error: string; message?: string };

/** GET /v1/settings/:child_id — read settings (with defaults). */
export async function getSettings(
  childId: number,
  sessionToken: string,
): Promise<GetSettingsResult> {
  try {
    const res = await apiFetch<{ ok: true; settings: AppSettings }>(
      `/v1/settings/${childId}`,
      { sessionToken },
    );
    return { ok: true, settings: res.settings };
  } catch (err) {
    const e = err as { status?: number; message?: string };
    return { ok: false, error: 'fetch_failed', message: e.message };
  }
}

export type UpdateSettingsInput = Partial<AppSettings>;

export type UpdateSettingsResult =
  | { ok: true; settings: AppSettings }
  | { ok: false; error: string; message?: string };

/** PUT /v1/settings/:child_id — update settings (strict partial, ≥1 field). */
export async function updateSettings(
  childId: number,
  sessionToken: string,
  input: UpdateSettingsInput,
): Promise<UpdateSettingsResult> {
  try {
    const res = await apiFetch<{ ok: true; settings: AppSettings }>(
      `/v1/settings/${childId}`,
      { method: 'PUT', body: input, sessionToken },
    );
    return { ok: true, settings: res.settings };
  } catch (err) {
    const e = err as { status?: number; message?: string };
    return { ok: false, error: 'update_failed', message: e.message };
  }
}

